/* eslint-disable react/no-unescaped-entities */
import { Meta } from '@/layout/Meta'
import { Main } from '@/templates/Main'
import { Code } from '@/utils/Code'
import { DessertIcon } from '@/utils/DessertIcon'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { note } from '@/utils/Note'
import { useEffect } from 'react'
import BlogCta from './blog-cta'

const TikTokLink = (props: {docId: string, children: string}) => {
  return (
    <Link href={`https://business-api.tiktok.com/portal/docs?id=${props.docId}`}>
      { props.children }
    </Link>
  )
}

const HowToIntegrateWithTikTokAds = () => {
  const router = useRouter()

  useEffect(() => {
    note('Visited TikTok tutorial')
  }, [])
  
  return (
    <Main meta={<Meta 
        title="How to Integrate with TikTok Ads"
        description="Connecting to TikTok Ads Reporting API" />}>
      <h1 className="text-4xl font-bold text-sky-700">TikTok Ads</h1>
      <h2>Reporting API</h2>
      <BlogCta />
      <div>
        <h2 className="text-4xl font-bold text-sky-700">Overview</h2>
        TikTok provides an easy <TikTokLink docId="1738864739862530"> Ads Reporting API</TikTokLink> for
        accessing your performance and spend data.  Like other advertisers, the effort is spent getting 
        approved for a developer token.
      </div>
      <div>
        TikTok provides a developer sandbox for safe development <DessertIcon rand={Math.random()}/>.
      </div>
      <div>
        The steps are:
        <ol className="list-decimal list-inside">
          <li>
            <TikTokLink docId="1735713609895937">
              Create and setup a TikTok Ads developer account
            </TikTokLink>
          </li>
          <li>Identify your advertiser ID</li>
          <li>Generate an OAuth access token</li>
          <li>Query the reporting API</li>
        </ol>
      </div>
      <div>
        <h2 className="text-4xl font-bold text-sky-700">Creating the TikTok Ads Developer Account</h2>
        I won't go into detail here as this guide is more about the technical implementation.  TikTok 
        has a thorough manual review process.
      </div>
      <div>
        You will need to provide information about your business use case, including your website and company
        location.
      </div>
      <div>
        <h2 className="text-4xl font-bold text-sky-700">Generate an OAuth access token</h2>
        Once you have a developer account, you can generate a standard OAuth access token.  
        
        <h3 className="text-xl font-bold text-sky-700 mt-2">Find your App ID</h3>
        You'll need your app ID found on your <Link href="https://business-api.tiktok.com/portal/apps">TikTok business account dashboard</Link>.
        <img className="mt-3" src={`${router.basePath}/assets/images/tiktok-app-id.png`} alt="TikTok App ID" />


        <h3 className="text-xl font-bold text-sky-700 mt-2">Authenticate your account</h3>
        Plug your app id into the following URL and open it in your browser:
        <Code>{`https://business-api.tiktok.com/portal/auth?app_id={{ your app id}}&state=your_custom_params&redirect_uri=http%3A%2F%2Flocalhost%3A3000`}</Code>
        <br/>
        You will be redirected to a broken localhost URL, but the URL will contain the authorization code.
        <Code>{`http://localhost:3000/?auth_code={{ ignore this code }}&code={{ copy this code }}&state=your_custom_params`}</Code>

        <h3 className="text-xl font-bold text-sky-700 mt-2">Generate an Access Token</h3>
        Post to the access_token endpoint
        <Code>{`
        POST https://business-api.tiktok.com/open_api/v1.3/oauth2/access_token/
        Content-Type:application/json
        
        {
            "secret": "{{$dotenv TIKTOK_SECRET}}", 
            "app_id": "{{$dotenv TIKTOK_APP_ID}}", 
            "auth_code": "{{ code copied from redirect URL }}"
        }
        `}</Code>

        <h3 className="text-xl font-bold text-sky-700 mt-2">Save the Access Token</h3>

        The access token returned <TikTokLink docId='1766037914914818'>does not expire.</TikTokLink>  So make sure to 
        store it somewhere secure.

        <Code>{`
        {
          "code": 0,
          "message": "OK",
          "request_id": "202309171942383471EF9E07779EA8057D",
          "data": {
            "access_token": "{{ save your access token }}",
            "advertiser_ids": [
              "{{ also note your advertiser ID }}"
            ],
            "scope": [
              4
            ]
          }
        }
        `}</Code>
      </div>
      <div>
        <h2 className="text-4xl font-bold text-sky-700">Identify your advertiser ID</h2>
        You will need your advertiser ID to query the&nbsp;
        <TikTokLink docId='1740302665828417'>reporting API</TikTokLink>.  If you didn't note this
        during the OAuth flow, you can find it by
        logging into your TikTok Ads account and expanding the Accounts drop down near the top right
        corner.

        <img className="mt-4" src={`${router.basePath}/assets/images/tiktok-advertiser-id.png`} alt="TikTok Advertiser ID" />
      </div>
      <div>
        <h2 className="text-4xl font-bold text-sky-700">Query the reporting API</h2>
        TikTok provides thorough documentation&nbsp;
        <TikTokLink docId='1740302848100353'>thorough documentation</TikTokLink> for their 
        reporting API.<br/>
        <br/>
        For example if you want to pull September's daily impressions and spend by campaign, you could 
        make the following query

        <Code>{`
        GET https://business-api.tiktok.com/open_api/v1.3/report/integrated/get/ HTTP/1.1
        Content-Type: application/x-www-form-urlencoded
        Access-Token: {{ your access token }}
        
        metrics=["spend", "impressions"]
        &data_level=AUCTION_CAMPAIGN
        &end_date=2023-09-30
        &page_size=10
        &start_date=2023-09-01
        &advertiser_id={{ YOUR ADVERTISER ID }}
        &service_type=AUCTION
        &report_type=BASIC
        &page=1
        &dimensions=["campaign_id", "stat_time_day"]
        `}</Code>

        The response
        
        <Code>{`
        {
          "code": 0,
          "message": "OK",
          "request_id": "202309241836164B1BB5FF1A45D79D5D4B",
          "data": {
            "list": [
              {
                "metrics": {
                  "impressions": "635",
                  "spend": "5.00"
                },
                "dimensions": {
                  "stat_time_day": "2023-09-15 00:00:00",
                  "campaign_id": "1774149501261857"
                }
              },
              {
                "metrics": {
                  "impressions": "1004",
                  "spend": "5.00"
                },
                "dimensions": {
                  "stat_time_day": "2023-09-16 00:00:00",
                  "campaign_id": "1774149501261857"
                }
              },
              ...
            ],
            "page_info": {
              "total_number": 3,
              "page": 1,
              "total_page": 1,
              "page_size": 3
            }
          }
        }
        `}</Code>
      </div>
    </Main>
  )
}

export default HowToIntegrateWithTikTokAds
